/**
 * Direct EPS Public referral API client (create-only, MVP Option C auth).
 *
 * Scope: create purchase and refinance referrals only. Status lookup, referral
 * detail, and referral update are intentionally NOT implemented for the MVP.
 *
 * Auth: the API key comes from runtime config and is sent on the configured
 * header. This key is browser-visible after deployment and is NOT a secret.
 */
import { formatApiKeyHeader, type RuntimeConfig } from "./config";
import type {
  CreatePurchaseReferralRequest,
  CreateRefinanceReferralRequest,
  CreateReferralResponse,
} from "./models/referral";

export const HEADER_CORRELATION_ID = "X-Correlation-Id";
export const HEADER_CLIENT_APP = "X-Client-App";

export type ApiErrorKind =
  | "config"
  | "validation"
  | "unauthorized"
  | "rateLimited"
  | "server"
  | "network"
  | "unknown";

export class ReferralApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;

  constructor(kind: ApiErrorKind, message: string, status?: number) {
    super(message);
    this.name = "ReferralApiError";
    this.kind = kind;
    if (status !== undefined) this.status = status;
  }
}

export interface ReferralApiClientOptions {
  fetchFn?: typeof fetch;
  correlationIdFactory?: () => string;
}

/**
 * Generates a browser-safe correlation id for a single submission. Exposed so
 * the submit flow can reuse one id for the EPS request header AND the outward
 * embedder postMessage.
 */
export function createCorrelationId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `tv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const FRIENDLY_MESSAGES: Record<ApiErrorKind, string> = {
  config: "The referral form is not configured correctly. Please contact support.",
  validation: "Some referral details were rejected. Please review the form and try again.",
  unauthorized: "This referral could not be authorized. Please contact your loanDepot administrator.",
  rateLimited: "Too many requests right now. Please wait a moment and try again.",
  server: "loanDepot could not process the referral right now. Please try again later.",
  network: "We could not reach loanDepot. Please check your connection and try again.",
  unknown: "Something went wrong while submitting the referral. Please try again.",
};

/**
 * Canonical, already-sanitized friendly message for an error kind. Never
 * includes raw response bodies, underlying exception text, or secrets, so it is
 * safe to surface both in the UI and in an outward postMessage.
 */
export function friendlyReferralMessage(kind: ApiErrorKind): string {
  return FRIENDLY_MESSAGES[kind];
}

/**
 * Whether the borrower can reasonably retry the same submission. Auth and
 * configuration problems require support, everything else is transient or
 * form-fixable.
 */
export function isRetryableKind(kind: ApiErrorKind): boolean {
  return kind !== "unauthorized" && kind !== "config";
}

function kindForStatus(status: number): Exclude<ApiErrorKind, "config" | "network"> {
  if (status === 400) return "validation";
  if (status === 401 || status === 403) return "unauthorized";
  if (status === 429) return "rateLimited";
  if (status >= 500) return "server";
  return "unknown";
}

export function parseCreateReferralResponse(raw: unknown): CreateReferralResponse {
  if (typeof raw !== "object" || raw === null) {
    throw new ReferralApiError("unknown", "Unexpected response from the referral API.");
  }
  const obj = raw as Record<string, unknown>;
  // EPS wraps successful bodies; accept either the raw model or a { data } envelope.
  const body =
    typeof obj.data === "object" && obj.data !== null
      ? (obj.data as Record<string, unknown>)
      : obj;

  const referralId = body.referralId;
  const status = body.status;
  const submittedAt = body.submittedAt;

  if (typeof referralId !== "string") {
    throw new ReferralApiError("unknown", "Referral response is missing 'referralId'.");
  }

  const response: CreateReferralResponse = {
    referralId,
    status: typeof status === "string" ? status : "Unknown",
    submittedAt: typeof submittedAt === "string" ? submittedAt : new Date().toISOString(),
  };

  if (typeof body.partnerReferenceId === "string") {
    response.partnerReferenceId = body.partnerReferenceId;
  }
  if (typeof body.links === "object" && body.links !== null) {
    const links = body.links as Record<string, unknown>;
    response.links = {};
    if (typeof links.status === "string") response.links.status = links.status;
    if (typeof links.detail === "string") response.links.detail = links.detail;
  }

  return response;
}

async function postReferral(
  path: string,
  request: unknown,
  config: RuntimeConfig,
  options: ReferralApiClientOptions,
): Promise<CreateReferralResponse> {
  if (!config.apiKey || config.apiKey.trim() === "") {
    throw new ReferralApiError(
      "config",
      "No API key is configured. Set 'apiKey' in the runtime config before submitting.",
    );
  }
  if (!config.apiBaseUrl || config.apiBaseUrl.trim() === "") {
    throw new ReferralApiError(
      "config",
      "No API base URL is configured. Set 'apiBaseUrl' in the runtime config.",
    );
  }

  const fetchFn = options.fetchFn ?? fetch;
  const correlationId = (options.correlationIdFactory ?? createCorrelationId)();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    [config.apiKeyHeaderName]: formatApiKeyHeader(config),
    [HEADER_CORRELATION_ID]: correlationId,
    [HEADER_CLIENT_APP]: config.clientAppName,
  };

  const url = `${config.apiBaseUrl}${path}`;

  let response: Response;
  try {
    response = await fetchFn(url, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    });
  } catch (err) {
    throw new ReferralApiError("network", FRIENDLY_MESSAGES.network + ` (${(err as Error).message})`);
  }

  if (!response.ok) {
    const kind = kindForStatus(response.status);
    throw new ReferralApiError(kind, FRIENDLY_MESSAGES[kind], response.status);
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new ReferralApiError("unknown", "The referral API returned a non-JSON success response.");
  }

  return parseCreateReferralResponse(raw);
}

export function createReferralClient(config: RuntimeConfig, options: ReferralApiClientOptions = {}) {
  return {
    createPurchaseReferral(
      request: CreatePurchaseReferralRequest,
    ): Promise<CreateReferralResponse> {
      return postReferral("/api/v1/referrals/purchase", request, config, options);
    },
    createRefinanceReferral(
      request: CreateRefinanceReferralRequest,
    ): Promise<CreateReferralResponse> {
      return postReferral("/api/v1/referrals/refinance", request, config, options);
    },
  };
}

export type ReferralApiClient = ReturnType<typeof createReferralClient>;
